import React, { useEffect, useState } from "react";
import AuthUser from "./AuthUser";
import toast, { Toaster } from "react-hot-toast";
import moment from "moment";
import { Link } from "react-router-dom";
import TaskSkeleton from "./TaskSkeleton";

export default function DueReportAll() {
    const { http, user } = AuthUser();
    const [tasks, setTasks] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    useEffect(() => {
        fetchTaskDetails();
    }, []);

    const fetchTaskDetails = () => {
        setIsLoading(true);
        let endpoint = `/due_report_all_students?company_id=${user.company_id}`;

        http
            .get(endpoint)
            .then((res) => {
                setIsLoading(false);
                if (Array.isArray(res.data.data)) {
                    setTasks(res.data.data);
                } else {
                    setIsLoading(false);
                    console.error("API response is not an array");
                }
            })
            .catch((error) => {
                setIsLoading(false);
                console.error("Error fetching tasks:", error);
            });
    };

    // Group tasks by year and sort
    const groupAndSortByYear = (data) => {
        const grouped = {};
        data.forEach(task => {
            const year = task.year || 'No Year';
            if (!grouped[year]) {
                grouped[year] = [];
            }
            grouped[year].push(task);
        });
        
        // Sort years in descending order (newest first), put "No Year" at the end
        const sortedYears = Object.keys(grouped).sort((a, b) => {
            if (a === 'No Year') return 1;
            if (b === 'No Year') return -1;
            const yearA = parseInt(a.split('-')[0]) || 0;
            const yearB = parseInt(b.split('-')[0]) || 0;
            return yearB - yearA;
        });
        
        return { grouped, sortedYears };
    };

    const { grouped, sortedYears } = groupAndSortByYear(tasks);
    
    // Calculate totals
    const totalAllStudents = tasks.reduce((sum, task) => sum + (task.all_students || 0), 0);
    const totalStudents = tasks.reduce((sum, task) => sum + (parseInt(task.total_students) || 0), 0);
    const totalDue = tasks.reduce((sum, task) => sum + (parseFloat(task.total_due) || 0), 0);
    const totalPaid = tasks.reduce((sum, task) => sum + (parseFloat(task.total_ext_6) || 0), 0);
    const totalMaster = tasks.reduce((sum, task) => sum + (parseFloat(task.total_master) || 0), 0);

    return (
        <>
            <Toaster />

            <div className="container mt-1">
                <div className="row">
                    <h3>Class Due Report (All Students)</h3>
                    <p className="text-muted">Shows all students from students table grouped by class, section & year</p>
                </div>

                <div className="table-responsive">
                    {isLoading ? (
                        <TaskSkeleton />
                    ) : (
                        <>
                            <table className="table table-striped">
                                <thead className="table-primary">
                                    <tr>
                                        <th className="text-danger">No</th>
                                        <th>Class</th>
                                        <th>Division</th>
                                        <th>All Students <br /><b>({totalAllStudents})</b></th>
                                        <th>Master Updated <br /><b>({totalStudents})</b></th>
                                        <th>Year</th>
                                        <th>Total Due <br /><b>({totalDue.toLocaleString('en-IN')})</b></th>
                                        <th>Total Paid <br /><b>({totalPaid.toLocaleString('en-IN')})</b></th>
                                        <th>Total Master <br /><b>({totalMaster.toLocaleString('en-IN')})</b></th>
                                        <th className="text-danger">Due %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedYears.map((year, yearIndex) => {
                                        const yearData = grouped[year];
                                        
                                        // Calculate year totals
                                        const yearTotalAllStudents = yearData.reduce((sum, task) => sum + (task.all_students || 0), 0);
                                        const yearTotalStudents = yearData.reduce((sum, task) => sum + (parseInt(task.total_students) || 0), 0);
                                        const yearTotalDue = yearData.reduce((sum, task) => sum + (parseFloat(task.total_due) || 0), 0);
                                        const yearTotalPaid = yearData.reduce((sum, task) => sum + (parseFloat(task.total_ext_6) || 0), 0);
                                        const yearTotalMaster = yearData.reduce((sum, task) => sum + (parseFloat(task.total_master) || 0), 0);
                                        const yearDuePercentage = yearTotalMaster > 0 ? Math.round(yearTotalDue / yearTotalMaster * 100) : 0;
                                        
                                        return (
                                            <React.Fragment key={year}>
                                                {/* Year Header Row */}
                                                <tr className={year === 'No Year' ? 'table-warning' : 'table-info'}>
                                                    <td colSpan="10" style={{textAlign: 'center', fontWeight: 'bold', fontSize: '16px'}}>
                                                        📅 {year === 'No Year' ? '⚠️ Students Without Year' : `Academic Year: ${year}`} ({yearData.length} rows)
                                                    </td>
                                                </tr>
                                                
                                                {/* Data rows for this year */}
                                                {yearData.map((taskuser, index) => (
                                                    <tr key={`${year}-${index}`} className={yearIndex % 2 === 0 ? 'table-light' : ''}>
                                                        <td>{index + 1}</td>
                                                        <td>{taskuser.class}</td>
                                                        <td>{taskuser.dev}</td>
                                                        <td><b>{taskuser.all_students || 0}</b></td>
                                                        <td><b>{taskuser.total_students || 0}</b></td>
                                                        <td><b>{taskuser.year || '-'}</b></td>
                                                        <td><b>{parseFloat(taskuser.total_due || 0).toLocaleString('en-IN')}</b></td>
                                                        <td><b>{parseFloat(taskuser.total_ext_6 || 0).toLocaleString('en-IN')}</b></td>
                                                        <td><b>{parseFloat(taskuser.total_master || 0).toLocaleString('en-IN')}</b></td>
                                                        <td>{parseFloat(taskuser.total_master) > 0 ? Math.round(parseFloat(taskuser.total_due) / parseFloat(taskuser.total_master) * 100) : 0}%</td>
                                                    </tr>
                                                ))}
                                                
                                                {/* Year Summary Row */}
                                                {yearData.length > 0 && (
                                                    <tr className="table-success" style={{fontWeight: 'bold', fontSize: '14px'}}>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px'}}>🎯</span>
                                                        </td>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px'}}>{year}</span>
                                                        </td>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px'}}>YEAR TOTAL</span>
                                                        </td>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px'}}>{yearTotalAllStudents}</span>
                                                        </td>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px'}}>{yearTotalStudents}</span>
                                                        </td>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px'}}>{year}</span>
                                                        </td>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px'}}>₹{yearTotalDue.toLocaleString('en-IN')}</span>
                                                        </td>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px'}}>₹{yearTotalPaid.toLocaleString('en-IN')}</span>
                                                        </td>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px'}}>₹{yearTotalMaster.toLocaleString('en-IN')}</span>
                                                        </td>
                                                        <td style={{textAlign: 'left', backgroundColor: '#d4edda', color: '#155724'}}>
                                                            <span style={{fontSize: '16px', fontWeight: 'bold'}}>{yearDuePercentage}%</span>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </>
                    )}
                </div>
            </div>
        </>
    );
}

